<template>
    <div class="contents">
        <div class="container-fluid">
            <div class="row">
                <div class="col-lg-12">
                    <div class="breadcrumb-main user-member justify-content-sm-between">
                        <h4 class="text-capitalize fw-500 breadcrumb-title">
                            <i class="fas fa-robot"></i> Statistik Token AI
                        </h4>
                        <div class="action-btn">
                            <button @click="loadList(); loadSummary();" class="btn btn-light">
                                <i class="fas fa-sync"></i> Refresh
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Stats Cards -->
            <div class="row mb-3">
                <div class="col-md-3">
                    <div class="card p-3 text-center">
                        <small class="text-muted">Token Hari Ini</small>
                        <h3 class="mb-0 text-primary"><i class="fas fa-bolt"></i> {{ formatToken(summary.total_token_today) }}</h3>
                    </div>
                </div>
                <div class="col-md-3">
                    <div class="card p-3 text-center">
                        <small class="text-muted">Token Bulan Ini</small>
                        <h3 class="mb-0 text-success"><i class="fas fa-calendar-alt"></i> {{ formatToken(summary.total_token_month) }}</h3>
                    </div>
                </div>
                <div class="col-md-3">
                    <div class="card p-3 text-center">
                        <small class="text-muted">User Aktif AI (Hari Ini)</small>
                        <h3 class="mb-0 text-info"><i class="fas fa-users"></i> {{ formatInt(summary.active_users_today) }}</h3>
                    </div>
                </div>
                <div class="col-md-3">
                    <div class="card p-3 text-center">
                        <small class="text-muted">Total Interaksi (Lifetime)</small>
                        <h3 class="mb-0 text-warning"><i class="fas fa-comments"></i> {{ formatInt(summary.total_interactions) }}</h3>
                    </div>
                </div>
            </div>

            <!-- Filters -->
            <div class="card p-3 mb-3">
                <div class="row">
                    <div class="col-md-5">
                        <label class="small">Cari Email / Phone</label>
                        <input v-model="filter.q" @input="onSearchInput" class="form-control form-control-sm"
                            placeholder="email@domain.com atau 0812...">
                    </div>
                    <div class="col-md-3">
                        <label class="small">Sort By</label>
                        <select v-model="filter.sort_by" @change="loadList" class="form-control form-control-sm">
                            <option value="token_lifetime">Token Lifetime</option>
                            <option value="token_today">Token Hari Ini</option>
                            <option value="token_month">Token Bulan Ini</option>
                            <option value="last_activity">Last Activity</option>
                        </select>
                    </div>
                    <div class="col-md-2">
                        <label class="small">Order</label>
                        <select v-model="filter.sort_dir" @change="loadList" class="form-control form-control-sm">
                            <option value="desc">Tertinggi</option>
                            <option value="asc">Terendah</option>
                        </select>
                    </div>
                    <div class="col-md-2 d-flex align-items-end">
                        <button @click="loadList" class="btn btn-sm btn-primary btn-block">
                            <i class="fas fa-search"></i> Cari
                        </button>
                    </div>
                </div>
            </div>

            <!-- Table -->
            <div v-if="loading" class="text-center p-5">
                <div class="spinner-border"></div>
            </div>
            <div v-else-if="items.length === 0" class="card p-5 text-center text-muted">
                <i class="fas fa-inbox fa-3x mb-3" style="color:#d1d5db;"></i>
                <h5>Tidak ada data user dengan AI bot</h5>
            </div>
            <div v-else class="card p-0" style="overflow-x:auto;">
                <table class="table table-hover mb-0" style="font-size:13px;">
                    <thead style="background:#f9fafb;">
                        <tr>
                            <th style="min-width:220px;">User</th>
                            <th>Paket</th>
                            <th class="text-center">Bot</th>
                            <th class="text-right">Token Hari Ini</th>
                            <th class="text-right">Token Bulan Ini</th>
                            <th class="text-right">Token Lifetime</th>
                            <th class="text-right">Kuota Respon Tersisa</th>
                            <th class="text-right">Sisa Quota Token</th>
                            <th class="text-right">Interaksi</th>
                            <th>Last Activity</th>
                            <th class="text-center">Aksi</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="row in items" :key="row.user_id">
                            <td>
                                <div><strong>{{ row.user_email }}</strong></div>
                                <small class="text-muted">{{ row.user_phone || '-' }}</small>
                                <span v-if="row.user_level" class="badge badge-light ml-1" style="font-size:10px;">{{ row.user_level }}</span>
                            </td>
                            <td><small>{{ row.package_name || '-' }}</small></td>
                            <td class="text-center"><span class="badge badge-secondary">{{ row.total_bots || 0 }}</span></td>
                            <td class="text-right">
                                <span :class="row.token_today > 0 ? 'text-primary font-weight-bold' : 'text-muted'">
                                    {{ formatToken(row.token_today) }}
                                </span>
                            </td>
                            <td class="text-right">
                                <span :class="row.token_month > 0 ? 'text-success' : 'text-muted'">
                                    {{ formatToken(row.token_month) }}
                                </span>
                            </td>
                            <td class="text-right">{{ formatToken(row.token_lifetime) }}</td>
                            <td class="text-right">
                                <template v-if="row.sharing_bots > 0">
                                    <span :class="responseQuotaClass(row.response_quota_remaining)">
                                        {{ formatInt(row.response_quota_remaining) }} Respon
                                    </span>
                                </template>
                                <span v-else class="text-muted">—</span>
                            </td>
                            <td class="text-right">
                                <template v-if="row.dedicated_bots > 0 && row.total_quota > 0">
                                    <span :class="quotaClass(row.quota_remaining, row.total_quota)">
                                        {{ formatToken(row.quota_remaining) }}
                                    </span>
                                    <br><small class="text-muted">/ {{ formatToken(row.total_quota) }}</small>
                                </template>
                                <template v-else-if="row.dedicated_bots > 0">
                                    <small class="text-muted">Penggunaan: {{ formatToken(row.total_usage) }}</small>
                                </template>
                                <span v-else class="text-muted">—</span>
                            </td>
                            <td class="text-right"><small>{{ formatInt(row.total_interactions_lifetime) }}</small></td>
                            <td><small>{{ formatTime(row.last_activity_at) }}</small></td>
                            <td class="text-center">
                                <button class="btn btn-sm btn-outline-primary" @click="openDetail(row)" title="Lihat Detail">
                                    <i class="fas fa-eye"></i>
                                </button>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <!-- Pagination -->
            <div v-if="total > limit" class="d-flex justify-content-between align-items-center mt-3">
                <small class="text-muted">Menampilkan {{ ((page - 1) * limit) + 1 }} - {{ Math.min(page * limit, total) }} dari {{ total }} user</small>
                <div>
                    <button @click="prevPage" :disabled="page <= 1" class="btn btn-sm btn-light">
                        <i class="fas fa-chevron-left"></i> Prev
                    </button>
                    <span class="mx-2">Page {{ page }} / {{ totalPages }}</span>
                    <button @click="nextPage" :disabled="page >= totalPages" class="btn btn-sm btn-light">
                        Next <i class="fas fa-chevron-right"></i>
                    </button>
                </div>
            </div>

            <!-- Detail Modal -->
            <modal name="ai-token-detail" :width="900" height="auto" :adaptive="true" :scrollable="true">
                <div v-if="loadingDetail" class="text-center p-5">
                    <div class="spinner-border"></div>
                    <p class="mt-2 text-muted">Memuat detail...</p>
                </div>
                <div v-else-if="detail" class="p-4">
                    <div class="d-flex justify-content-between align-items-start mb-3">
                        <div>
                            <h4 class="mb-1">{{ detail.user.email }}</h4>
                            <small class="text-muted">
                                {{ detail.user.phone || '-' }} ·
                                <span class="badge badge-light">{{ detail.user.level }}</span> ·
                                {{ detail.user.package_name || 'No Package' }}
                            </small>
                        </div>
                        <button class="btn btn-sm btn-light" @click="$modal.hide('ai-token-detail')">
                            <i class="fas fa-times"></i>
                        </button>
                    </div>

                    <!-- Totals -->
                    <div class="row mb-3">
                        <div class="col-md-3">
                            <div class="card p-2 text-center">
                                <small class="text-muted">Token Hari Ini</small>
                                <strong class="text-primary">{{ formatToken(detail.totals.token_today) }}</strong>
                            </div>
                        </div>
                        <div class="col-md-3">
                            <div class="card p-2 text-center">
                                <small class="text-muted">Token Bulan Ini</small>
                                <strong class="text-success">{{ formatToken(detail.totals.token_month) }}</strong>
                            </div>
                        </div>
                        <div class="col-md-3">
                            <div class="card p-2 text-center">
                                <small class="text-muted">Token Lifetime</small>
                                <strong>{{ formatToken(detail.totals.token_lifetime) }}</strong>
                            </div>
                        </div>
                        <div class="col-md-3" v-if="detail.totals.sharing_bots > 0">
                            <div class="card p-2 text-center">
                                <small class="text-muted">Kuota Respon Tersisa</small>
                                <strong :class="responseQuotaClass(detail.totals.response_quota_remaining)">
                                    {{ formatInt(detail.totals.response_quota_remaining) }} Respon
                                </strong>
                            </div>
                        </div>
                        <div class="col-md-3" v-if="detail.totals.dedicated_bots > 0">
                            <div class="card p-2 text-center">
                                <small class="text-muted">Sisa Quota Token</small>
                                <strong v-if="detail.totals.total_quota > 0" :class="detail.totals.quota_remaining < 0 ? 'text-danger' : 'text-success'">
                                    {{ formatToken(detail.totals.quota_remaining) }}
                                </strong>
                                <strong v-else class="text-muted">{{ formatToken(detail.totals.total_usage) }} terpakai</strong>
                            </div>
                        </div>
                    </div>

                    <!-- Bots -->
                    <h6 class="mt-3"><i class="fas fa-robot"></i> Bot AI ({{ detail.bots.length }})</h6>
                    <div class="table-responsive">
                        <table class="table table-sm table-bordered" style="font-size:12px;">
                            <thead style="background:#f9fafb;">
                                <tr>
                                    <th>Nama Bot</th>
                                    <th>Model</th>
                                    <th>Device</th>
                                    <th class="text-right">Kuota Respon</th>
                                    <th class="text-right">Quota / Usage</th>
                                    <th class="text-right">Interaksi</th>
                                    <th>Last Used</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="b in detail.bots" :key="b.bot_id">
                                    <td><strong>{{ b.bot_name }}</strong></td>
                                    <td><code style="font-size:10px;">{{ b.model }}</code></td>
                                    <td><small>{{ b.device_name || '-' }}</small></td>
                                    <td class="text-right">
                                        <span v-if="b.bot_type === 2">{{ formatInt(b.response_quota_remaining) }} Respon</span>
                                        <span v-else class="text-muted">—</span>
                                    </td>
                                    <td class="text-right">
                                        <template v-if="b.bot_type === 2">
                                            <small class="text-muted">{{ formatInt(b.quota) }} poin</small>
                                        </template>
                                        <template v-else>
                                            {{ formatToken(b.usage) }}
                                        </template>
                                    </td>
                                    <td class="text-right">{{ formatInt(b.total_interactions) }}</td>
                                    <td><small>{{ formatTime(b.last_used_at) }}</small></td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <!-- Daily breakdown -->
                    <h6 class="mt-3"><i class="fas fa-chart-line"></i> 30 Hari Terakhir</h6>
                    <div v-if="detail.daily && detail.daily.length" class="table-responsive" style="max-height:200px;overflow-y:auto;">
                        <table class="table table-sm table-bordered" style="font-size:12px;">
                            <thead style="background:#f9fafb;position:sticky;top:0;">
                                <tr><th>Tanggal</th><th class="text-right">Token</th><th class="text-right">Interaksi</th></tr>
                            </thead>
                            <tbody>
                                <tr v-for="d in detail.daily" :key="d.date">
                                    <td>{{ d.date }}</td>
                                    <td class="text-right">{{ formatToken(d.token) }}</td>
                                    <td class="text-right">{{ formatInt(d.interactions) }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <p v-else class="text-muted small">Tidak ada aktivitas dalam 30 hari terakhir.</p>

                    <!-- Conversations -->
                    <h6 class="mt-3"><i class="fas fa-comment-dots"></i> 50 Percakapan Terakhir</h6>
                    <div v-if="detail.conversations && detail.conversations.length" class="table-responsive" style="max-height:300px;overflow-y:auto;">
                        <table class="table table-sm table-bordered" style="font-size:11px;">
                            <thead style="background:#f9fafb;position:sticky;top:0;">
                                <tr>
                                    <th>Waktu</th>
                                    <th>Nomor</th>
                                    <th>Pertanyaan</th>
                                    <th>Jawaban</th>
                                    <th class="text-right">Token</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="c in detail.conversations" :key="c.id">
                                    <td><small>{{ formatTime(c.created_at) }}</small></td>
                                    <td><small>{{ c.number }}</small></td>
                                    <td><small>{{ c.question }}</small></td>
                                    <td><small>{{ c.answer }}</small></td>
                                    <td class="text-right">{{ formatToken(c.total_token) }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <p v-else class="text-muted small">Belum ada percakapan.</p>
                </div>
                <div v-else class="p-5 text-center text-muted">
                    <i class="fas fa-exclamation-circle fa-2x"></i>
                    <p class="mt-2">Gagal memuat detail.</p>
                </div>
            </modal>
        </div>
    </div>
</template>

<script>
import moment from 'moment';
import axios from 'axios';

export default {
    data() {
        return {
            loading: false,
            loadingDetail: false,
            items: [],
            total: 0,
            page: 1,
            limit: 25,
            summary: {
                total_token_today: 0,
                total_token_month: 0,
                active_users_today: 0,
                total_interactions: 0,
            },
            filter: {
                q: '',
                sort_by: 'token_lifetime',
                sort_dir: 'desc',
            },
            searchTimer: null,
            detail: null,
        };
    },
    computed: {
        totalPages() {
            return Math.ceil(this.total / this.limit) || 1;
        }
    },
    mounted() {
        this.loadList();
    },
    methods: {
        async loadList() {
            this.loading = true;
            try {
                const params = {
                    page: this.page,
                    limit: this.limit,
                    sort_by: this.filter.sort_by,
                    sort_dir: this.filter.sort_dir,
                };
                if (this.filter.q) params.q = this.filter.q;
                const r = await axios.get('admin/ai-token-stats', { params });
                if (r.data && r.data.success) {
                    this.items = r.data.data.items || [];
                    this.total = r.data.data.total || 0;
                    if (r.data.data.summary) {
                        this.summary = r.data.data.summary;
                    }
                }
            } catch (err) {
                console.error('Failed to load AI token stats', err);
                const msg = (err.response && err.response.data && err.response.data.message) || err.message;
                if (this.$toast) this.$toast.error('Gagal memuat data: ' + msg);
            } finally {
                this.loading = false;
            }
        },
        async loadSummary() {
            // Summary di-load sekaligus saat loadList. Method ini untuk refresh button.
            await this.loadList();
        },
        onSearchInput() {
            if (this.searchTimer) clearTimeout(this.searchTimer);
            this.searchTimer = setTimeout(() => {
                this.page = 1;
                this.loadList();
            }, 400);
        },
        prevPage() {
            if (this.page > 1) {
                this.page--;
                this.loadList();
            }
        },
        nextPage() {
            if (this.page < this.totalPages) {
                this.page++;
                this.loadList();
            }
        },
        async openDetail(row) {
            this.detail = null;
            this.loadingDetail = true;
            this.$modal.show('ai-token-detail');
            try {
                const r = await axios.get(`admin/ai-token-stats/${row.user_id}/detail`);
                if (r.data && r.data.success) {
                    this.detail = r.data.data;
                }
            } catch (err) {
                console.error('Failed to load detail', err);
                const msg = (err.response && err.response.data && err.response.data.message) || err.message;
                if (this.$toast) this.$toast.error('Gagal memuat detail: ' + msg);
            } finally {
                this.loadingDetail = false;
            }
        },
        formatToken(v) {
            if (v === null || v === undefined) return '0';
            const n = Number(v);
            if (Number.isNaN(n)) return '0';
            if (Math.abs(n) >= 100) return n.toLocaleString('id-ID', { maximumFractionDigits: 2 });
            return n.toLocaleString('id-ID', { maximumFractionDigits: 4 });
        },
        formatInt(v) {
            if (v === null || v === undefined) return '0';
            return Number(v).toLocaleString('id-ID');
        },
        formatTime(ms) {
            if (!ms || Number(ms) === 0) return '-';
            return moment(Number(ms)).format('DD MMM YYYY HH:mm');
        },
        quotaClass(remaining, total) {
            if (!total || total === 0) return 'text-muted';
            if (remaining < 0) return 'text-danger font-weight-bold';
            const pct = (remaining / total) * 100;
            if (pct < 10) return 'text-danger';
            if (pct < 25) return 'text-warning';
            return 'text-success';
        },
        responseQuotaClass(responses) {
            const n = Number(responses || 0);
            if (n <= 0) return 'text-danger font-weight-bold';
            if (n < 100) return 'text-warning';
            return 'text-success';
        }
    }
};
</script>
